import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, ApiError } from '../../services/api'
import { colors } from '../../components/ui/styles'

const cardStyle = {
  padding: '1.5rem',
  background: colors.bgCard,
  borderRadius: '8px',
  border: `1px solid ${colors.border}`,
}

const inputStyle = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: `1px solid #d1d5db`,
  borderRadius: '6px',
  fontSize: '0.875rem',
  boxSizing: 'border-box' as const,
}

const labelStyle = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: 500 as const,
  marginBottom: '0.25rem',
  color: '#374151',
}

const btnStyle = {
  padding: '0.5rem 1rem',
  borderRadius: '6px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: 500 as const,
}

interface InstanceOption {
  readonly value: string
  readonly label: string
  readonly default?: boolean
}

interface ProviderConfig {
  readonly id: string
  readonly label: string
  readonly regions: readonly string[]
  readonly instanceTypes: readonly InstanceOption[]
}

interface CustomerRow {
  readonly id: string
  readonly name: string
  readonly contactEmail: string | null
}

interface ProvisionStep {
  readonly step: string
  status: 'pending' | 'running' | 'done' | 'failed'
  elapsedMs?: number
  message?: string
}

interface JobStatus {
  readonly status: 'running' | 'awaiting_dns' | 'completed' | 'failed'
  readonly steps: readonly {
    readonly step: string
    readonly status: 'pending' | 'running' | 'done' | 'failed'
    readonly message?: string
    readonly startedAt?: string | null
    readonly completedAt?: string | null
  }[]
  readonly result?: {
    readonly publicIp: string
    readonly domain?: string | null
    readonly plaintextKey: string
    readonly environmentId: string
  } | null
  readonly error?: string | null
  readonly environmentId?: string | null
}

const TOTAL_STEPS = 5
const STEP_LABELS = ['Provider', 'Customer', 'Review', 'Infrastructure', 'Installation']

// Display names for the API's provision step ids (ProvisionStep.step).
const PROVISION_STEP_LABELS: Record<string, string> = {
  'generate-keypair': 'Generate keypair',
  'create-vm': 'Create VM',
  'allocate-ip': 'Allocate IP',
  'wait-ssh': 'Wait for SSH',
  'create-environment': 'Create environment',
  'store-config': 'Store config',
  'install-base': 'Install base',
  'configure-env': 'Configure env',
  'apply-install': 'Apply install',
  'configure-tls': 'Configure TLS',
  'activate-license': 'Activate license',
  'enable-backup': 'Enable backup',
  'verify-heartbeat': 'Verify heartbeat',
}

const stepLabel = (step: string): string => PROVISION_STEP_LABELS[step] ?? step

const stepElapsedMs = (s: { startedAt?: string | null; completedAt?: string | null }): number | undefined =>
  s.startedAt && s.completedAt ? new Date(s.completedAt).getTime() - new Date(s.startedAt).getTime() : undefined

function maskCredential(label: string, value: string): string {
  if (!value) return ''
  const visible = value.slice(-4)
  return `${label} (****${visible})`
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
}

export function ProvisionWizardPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const resumeEnvId = searchParams.get('resume')

  const [providerConfigs, setProviderConfigs] = useState<ProviderConfig[]>([])
  const [providersLoading, setProvidersLoading] = useState(true)

  useEffect(() => {
    api
      .get<ProviderConfig[]>('/admin/settings/provisioning-providers')
      .then((res) => setProviderConfigs(res.data ?? []))
      .catch(() => {})
      .finally(() => setProvidersLoading(false))
  }, [])

  const [step, setStep] = useState(resumeEnvId ? 4 : 1)
  const [stepError, setStepError] = useState<string | null>(null)

  // Step 1 — provider + VM
  const [provider, setProvider] = useState<string | null>(null)
  const [accessKeyId, setAccessKeyId] = useState('')
  const [secretAccessKey, setSecretAccessKey] = useState('')
  const [apiToken, setApiToken] = useState('')
  const [vmName, setVmName] = useState('')
  const [region, setRegion] = useState('')
  const [instanceType, setInstanceType] = useState('')
  const [diskSize, setDiskSize] = useState(30)
  const [testingCreds, setTestingCreds] = useState(false)
  const [credTestResult, setCredTestResult] = useState<{ success: boolean; message: string } | null>(null)

  // Step 2 — customer
  const [customers, setCustomers] = useState<readonly CustomerRow[]>([])
  const [customersLoading, setCustomersLoading] = useState(false)
  const [selectedCustomerId, setSelectedCustomerId] = useState('')
  const [createNewOrg, setCreateNewOrg] = useState(false)
  const [newOrgName, setNewOrgName] = useState('')
  const [newOrgEmail, setNewOrgEmail] = useState('')
  const [environmentName, setEnvironmentName] = useState('')
  const [tenantName, setTenantName] = useState('')
  const [tenantAdminEmail, setTenantAdminEmail] = useState('')
  const [tenantAdminPassword, setTenantAdminPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [customerDomain, setCustomerDomain] = useState('')
  const [acmeEmail, setAcmeEmail] = useState('ops@medflowllc.com')
  const [enableBackup, setEnableBackup] = useState(true)

  // Step 4 — infrastructure / DNS pause
  const [jobId, setJobId] = useState<string | null>(null)
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null)
  const [provisionSteps, setProvisionSteps] = useState<ProvisionStep[]>([])
  const [copiedKey, setCopiedKey] = useState(false)

  // Step output viewer
  const [expandedStep, setExpandedStep] = useState<number | null>(null)
  const [stepEvents, setStepEvents] = useState<
    Record<
      string,
      { stdout?: string; stderr?: string; exitCode?: number; command?: string; durationMs?: number } | null
    >
  >({})

  // DNS pause state (between phase 1 and phase 2)
  const [awaitingDns, setAwaitingDns] = useState(false)
  const [phase1Result, setPhase1Result] = useState<{
    publicIp: string
    domain: string | null
    licenseKey: string
    environmentId: string
  } | null>(null)
  const [resuming, setResuming] = useState(false)

  // Handle resume mode — load environment details and go directly to DNS pause
  useEffect(() => {
    if (!resumeEnvId) return
    let cancelled = false
    api
      .get<{
        environment: {
          id: string
          publicIp: string | null
          provisioningStatus: string | null
          name: string
        }
      }>(`/fleet/environments/${resumeEnvId}`)
      .then((res) => {
        if (cancelled || !res.data) return
        const env = res.data.environment
        if (env.provisioningStatus !== 'awaiting_dns') {
          setStepError('This environment is not awaiting DNS setup.')
          return
        }
        setAwaitingDns(true)
        setPhase1Result({
          publicIp: env.publicIp ?? '',
          domain: null,
          licenseKey: '(stored — shown after installation completes)',
          environmentId: env.id,
        })
        setStep(4)
      })
      .catch(() => setStepError('Failed to load environment for resume.'))
    return () => {
      cancelled = true
    }
  }, [resumeEnvId])

  useEffect(() => {
    if (!jobId || !step || step < 4) return
    const interval = setInterval(async () => {
      try {
        const res = await api.get<JobStatus>(`/fleet/provision/${jobId}`)
        const data = res.data
        if (!data) return
        setJobStatus(data)
        setProvisionSteps(
          data.steps.map((s) => ({
            step: s.step,
            status: s.status,
            elapsedMs: stepElapsedMs(s),
            message: s.message,
          })),
        )
        if (data.status === 'awaiting_dns' && data.result) {
          setAwaitingDns(true)
          setPhase1Result({
            publicIp: data.result.publicIp,
            domain: data.result.domain ?? null,
            licenseKey: data.result.plaintextKey,
            environmentId: data.result.environmentId,
          })
          clearInterval(interval)
        }
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(interval)
        }
      } catch {
        // poll failures are non-fatal
      }
    }, 2000)
    return () => clearInterval(interval)
  }, [jobId, step])

  const providerCredentials = (): Record<string, string> => {
    if (!provider) return {}
    if (provider === 'aws') return { accessKeyId, secretAccessKey }
    return { apiToken }
  }

  const validateStep1 = (): string | null => {
    if (!provider) return 'Select a cloud provider.'
    if (provider === 'aws') {
      if (!accessKeyId.trim()) return 'Access Key ID is required.'
      if (!secretAccessKey.trim()) return 'Secret Access Key is required.'
    } else {
      if (!apiToken.trim()) return 'API Token is required.'
    }
    if (!vmName.trim()) return 'VM name is required.'
    if (!region) return 'Region is required.'
    if (!instanceType) return 'Instance type is required.'
    return null
  }

  const validateStep2 = (): string | null => {
    if (!createNewOrg && !selectedCustomerId) return 'Select an existing customer or create a new one.'
    if (createNewOrg) {
      if (!newOrgName.trim()) return 'Organization name is required.'
      if (!newOrgEmail.trim()) return 'Organization contact email is required.'
    }
    if (!environmentName.trim()) return 'Environment name is required.'
    if (!tenantName.trim()) return 'Tenant name is required.'
    if (!tenantAdminEmail.trim()) return 'Tenant admin email is required.'
    if (!tenantAdminPassword.trim()) return 'Tenant admin password is required.'
    if (tenantAdminPassword.length < 8) return 'Tenant admin password must be at least 8 characters.'
    return null
  }

  const activeProvider = providerConfigs.find((c) => c.id === provider)

  const handleProviderChange = (p: string) => {
    setProvider(p)
    setRegion('')
    const cfg = providerConfigs.find((c) => c.id === p)
    const defaultType = cfg?.instanceTypes.find((t) => t.default)
    setInstanceType(defaultType?.value ?? cfg?.instanceTypes[0]?.value ?? '')
    setCredTestResult(null)
  }

  const handleTestCredentials = async () => {
    const err = validateStep1()
    if (err) {
      setStepError(err)
      return
    }
    setTestingCreds(true)
    setCredTestResult(null)
    try {
      const res = await api.post<{ success: boolean; message: string }>('/fleet/provision/test-credentials', {
        provider,
        credentials: providerCredentials(),
        region,
      })
      setCredTestResult(res.data ?? { success: false, message: 'No response' })
    } catch (err) {
      setCredTestResult({ success: false, message: err instanceof ApiError ? err.message : 'Test failed' })
    } finally {
      setTestingCreds(false)
    }
  }

  const handleNext = async () => {
    setStepError(null)
    if (step === 1) {
      const err = validateStep1()
      if (err) {
        setStepError(err)
        return
      }
      setStep(2)
    } else if (step === 2) {
      const err = validateStep2()
      if (err) {
        setStepError(err)
        return
      }
      setStep(3)
    } else if (step === 3) {
      await handleProvision()
    }
  }

  const handleBack = () => {
    setStepError(null)
    setStep((s) => Math.max(1, s - 1))
  }

  const handleProvision = async () => {
    setStepError(null)
    try {
      const res = await api.post<{ jobId: string }>('/fleet/provision', {
        provider,
        credentials: providerCredentials(),
        vmConfig: { name: vmName, region, instanceType, diskSizeGb: diskSize },
        customerConfig: {
          organizationId: createNewOrg ? null : selectedCustomerId,
          organizationName: createNewOrg ? newOrgName : undefined,
          contactEmail: createNewOrg ? newOrgEmail : undefined,
          environmentName,
          tenantName,
          tenantAdminEmail,
          tenantAdminPassword,
          customerDomain: customerDomain || undefined,
          acmeEmail,
        },
        backupConfig: { enabled: enableBackup },
      })
      if (res.data?.jobId) {
        setJobId(res.data.jobId)
        setProvisionSteps([])
        setStep(4)
      }
    } catch (err) {
      setStepError(err instanceof ApiError ? err.message : 'Failed to start provisioning')
    }
  }

  const handleResume = async () => {
    if (!phase1Result) return
    setResuming(true)
    setStepError(null)
    setAwaitingDns(false)
    try {
      const res = await api.post<{ jobId: string }>(
        `/fleet/environments/${phase1Result.environmentId}/resume-provision`,
        {},
      )
      if (res.data?.jobId) {
        setJobId(res.data.jobId)
        setProvisionSteps([])
        setJobStatus(null)
        setStep(5)
      }
    } catch (err) {
      setStepError(err instanceof ApiError ? err.message : 'Failed to resume provisioning')
      setAwaitingDns(true)
    } finally {
      setResuming(false)
    }
  }

  const handleCopyKey = async () => {
    const key = jobStatus?.result?.plaintextKey ?? phase1Result?.licenseKey
    if (!key) return
    try {
      await navigator.clipboard.writeText(key)
      setCopiedKey(true)
      setTimeout(() => setCopiedKey(false), 2000)
    } catch {
      setCopiedKey(false)
    }
  }

  const handleStepClick = async (idx: number, stepId: string) => {
    const s = provisionSteps[idx]
    if (!s || (s.status !== 'done' && s.status !== 'failed')) return
    if (expandedStep === idx) {
      setExpandedStep(null)
      return
    }
    setExpandedStep(idx)
    if (stepEvents[stepId] !== undefined) return
    const envId = phase1Result?.environmentId ?? jobStatus?.result?.environmentId ?? jobStatus?.environmentId
    if (!envId) return
    try {
      const res = await api.get<
        readonly {
          details: { stdout?: string; stderr?: string; exitCode?: number; command?: string; durationMs?: number }
        }[]
      >(`/admin/operational-events?source=provisioning&entityType=provision_step&entityId=${envId}`)
      const match = (res.data ?? []).find((e) => e.details && (e.details as Record<string, unknown>).step === stepId)
      setStepEvents((prev) => ({ ...prev, [stepId]: match?.details ?? null }))
    } catch {
      setStepEvents((prev) => ({ ...prev, [stepId]: null }))
    }
  }

  useEffect(() => {
    if (step === 2 && customers.length === 0 && !customersLoading) {
      setCustomersLoading(true)
      api
        .get<readonly CustomerRow[]>('/fleet/customers')
        .then((res) => setCustomers(res.data ?? []))
        .catch(() => setCustomers([]))
        .finally(() => setCustomersLoading(false))
    }
  }, [step, customers.length, customersLoading])

  const renderStep1 = () => (
    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '1.25rem' }}>
      <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem' }}>Cloud Provider &amp; VM Configuration</h2>

      <div>
        <div style={labelStyle}>Cloud Provider</div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' as const }}>
          {providersLoading ? (
            <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading providers...</span>
          ) : (
            providerConfigs.map((pc) => {
              const active = provider === pc.id
              return (
                <button
                  key={pc.id}
                  onClick={() => handleProviderChange(pc.id)}
                  style={{
                    padding: '0.75rem 1.25rem',
                    borderRadius: '8px',
                    border: `2px solid ${active ? colors.primary : colors.border}`,
                    background: active ? '#eff6ff' : colors.bgCard,
                    cursor: 'pointer',
                    fontWeight: active ? 600 : 400,
                    color: active ? colors.primary : '#374151',
                    fontSize: '0.875rem',
                    minWidth: '120px',
                  }}
                  data-testid={`provider-card-${pc.id}`}
                >
                  {pc.label}
                </button>
              )
            })
          )}
        </div>
      </div>

      {provider === 'aws' && (
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '0.75rem' }}>
          <div>
            <label style={labelStyle}>Access Key ID</label>
            <input
              style={inputStyle}
              value={accessKeyId}
              onChange={(e) => setAccessKeyId(e.target.value)}
              placeholder="AKIAIOSFODNN7EXAMPLE"
              data-testid="aws-access-key-id"
            />
          </div>
          <div>
            <label style={labelStyle}>Secret Access Key</label>
            <input
              type="password"
              style={inputStyle}
              value={secretAccessKey}
              onChange={(e) => setSecretAccessKey(e.target.value)}
              data-testid="aws-secret-access-key"
            />
          </div>
        </div>
      )}

      {provider && provider !== 'aws' && (
        <div>
          <label style={labelStyle}>API Token</label>
          <input
            type="password"
            style={inputStyle}
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            data-testid="api-token"
          />
        </div>
      )}

      {provider && (
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '0.75rem' }}>
          <hr style={{ margin: '0', border: 'none', borderTop: `1px solid ${colors.border}` }} />

          <div>
            <label style={labelStyle}>VM Name</label>
            <input
              style={inputStyle}
              value={vmName}
              onChange={(e) => setVmName(slugify(e.target.value))}
              placeholder="acme-lab-prod"
              data-testid="vm-name"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Region</label>
              <select
                style={{ ...inputStyle }}
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                data-testid="vm-region"
              >
                <option value="">-- Select region --</option>
                {(activeProvider?.regions ?? []).map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Instance Type</label>
              <select
                style={{ ...inputStyle }}
                value={instanceType}
                onChange={(e) => setInstanceType(e.target.value)}
                data-testid="vm-instance-type"
              >
                <option value="">-- Select --</option>
                {(activeProvider?.instanceTypes ?? []).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                    {opt.default ? ' (Recommended)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Disk Size (GB)</label>
            <input
              type="number"
              min={30}
              style={{ ...inputStyle, width: '160px' }}
              value={diskSize}
              onChange={(e) => setDiskSize(Math.max(30, parseInt(e.target.value) || 30))}
              data-testid="vm-disk-size"
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' }}>
            <button
              onClick={handleTestCredentials}
              disabled={testingCreds}
              style={{ ...btnStyle, background: '#f1f5f9', color: '#374151', opacity: testingCreds ? 0.6 : 1 }}
              data-testid="test-credentials-btn"
            >
              {testingCreds ? 'Testing...' : 'Test Credentials'}
            </button>
            {credTestResult && (
              <span
                style={{
                  fontSize: '0.8125rem',
                  color: credTestResult.success ? '#16a34a' : '#dc2626',
                }}
              >
                {credTestResult.success ? 'Credentials valid' : credTestResult.message}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )

  const renderStep2 = () => (
    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '1rem' }}>
      <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem' }}>Customer Configuration</h2>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>Organization</label>
          <label
            style={{
              fontSize: '0.8125rem',
              color: colors.textMuted,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
            }}
          >
            <input
              type="checkbox"
              checked={createNewOrg}
              onChange={(e) => {
                setCreateNewOrg(e.target.checked)
                setSelectedCustomerId('')
              }}
              data-testid="create-new-org-toggle"
            />
            Create new organization
          </label>
        </div>
        {!createNewOrg ? (
          <select
            style={{ ...inputStyle }}
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(e.target.value)}
            disabled={customersLoading}
            data-testid="org-select"
          >
            <option value="">-- Select customer --</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Organization Name</label>
              <input
                style={inputStyle}
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                placeholder="Acme Medical Lab"
                data-testid="new-org-name"
              />
            </div>
            <div>
              <label style={labelStyle}>Contact Email</label>
              <input
                type="email"
                style={inputStyle}
                value={newOrgEmail}
                onChange={(e) => setNewOrgEmail(e.target.value)}
                placeholder="admin@acmelab.com"
                data-testid="new-org-email"
              />
            </div>
          </div>
        )}
      </div>

      <hr style={{ margin: '0', border: 'none', borderTop: `1px solid ${colors.border}` }} />

      <div>
        <label style={labelStyle}>Environment Name</label>
        <input
          style={inputStyle}
          value={environmentName}
          onChange={(e) => setEnvironmentName(e.target.value)}
          placeholder="Acme Lab — Main Site"
          data-testid="environment-name"
        />
      </div>

      <div>
        <label style={labelStyle}>Tenant Name</label>
        <input
          style={inputStyle}
          value={tenantName}
          onChange={(e) => setTenantName(e.target.value)}
          placeholder="acmelab"
          data-testid="tenant-name"
        />
      </div>

      <div>
        <label style={labelStyle}>Tenant Admin Email</label>
        <input
          type="email"
          style={inputStyle}
          value={tenantAdminEmail}
          onChange={(e) => setTenantAdminEmail(e.target.value)}
          placeholder="admin@acmelab.com"
          data-testid="tenant-admin-email"
        />
      </div>

      <div>
        <label style={labelStyle}>Tenant Admin Password</label>
        <div style={{ position: 'relative' as const }}>
          <input
            type={showPassword ? 'text' : 'password'}
            style={{ ...inputStyle, paddingRight: '5rem' }}
            value={tenantAdminPassword}
            onChange={(e) => setTenantAdminPassword(e.target.value)}
            data-testid="tenant-admin-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            style={{
              position: 'absolute' as const,
              right: '0.5rem',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.75rem',
              color: colors.textMuted,
              padding: '0.25rem',
            }}
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>
        {tenantAdminPassword && tenantAdminPassword.length < 8 && (
          <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem' }}>
            Minimum 8 characters required
          </div>
        )}
      </div>

      <hr style={{ margin: '0', border: 'none', borderTop: `1px solid ${colors.border}` }} />

      <div>
        <label style={labelStyle}>
          Customer Domain <span style={{ fontWeight: 400, color: colors.textMuted }}>(optional)</span>
        </label>
        <input
          style={inputStyle}
          value={customerDomain}
          onChange={(e) => setCustomerDomain(e.target.value)}
          placeholder="acme.medflowllc.com"
          data-testid="customer-domain"
        />
      </div>

      <div>
        <label style={labelStyle}>
          Let&apos;s Encrypt Email{' '}
          <span style={{ fontWeight: 400, color: colors.textMuted }}>(ACME account for TLS certificate notices)</span>
        </label>
        <input
          type="email"
          style={inputStyle}
          value={acmeEmail}
          onChange={(e) => setAcmeEmail(e.target.value)}
          data-testid="acme-email"
        />
      </div>

      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          cursor: 'pointer',
          fontSize: '0.875rem',
          color: '#374151',
        }}
      >
        <input
          type="checkbox"
          checked={enableBackup}
          onChange={(e) => setEnableBackup(e.target.checked)}
          data-testid="enable-backup"
        />
        Enable off-site backup
      </label>
    </div>
  )

  const renderStep3 = () => {
    const orgLabel = createNewOrg
      ? newOrgName
      : (customers.find((c) => c.id === selectedCustomerId)?.name ?? selectedCustomerId)

    const credLabel = provider === 'aws' ? maskCredential('AWS', accessKeyId) : maskCredential('Token', apiToken)

    const rows: readonly [string, string][] = [
      ['Provider', activeProvider?.label ?? provider!],
      ['Credentials', credLabel],
      ['VM Name', vmName],
      ['Region', region],
      ['Instance Type', instanceType],
      ['Disk Size', `${diskSize} GB`],
      ['Organization', orgLabel],
      ['Environment Name', environmentName],
      ['Tenant Name', tenantName],
      ['Tenant Admin Email', tenantAdminEmail],
      ['Customer Domain', customerDomain || '(none)'],
      ["Let's Encrypt Email", acmeEmail],
      ['Backup Enabled', enableBackup ? 'Yes' : 'No'],
    ]

    return (
      <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '1rem' }}>
        <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem' }}>Review Configuration</h2>

        <div
          style={{
            padding: '0.75rem',
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: '6px',
            color: '#92400e',
            fontSize: '0.8125rem',
          }}
        >
          Cloud credentials are used only for this provisioning run and are not stored.
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.875rem' }}>
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k} style={{ borderBottom: `1px solid ${colors.border}` }}>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500, color: '#374151', width: '40%' }}>{k}</td>
                <td style={{ padding: '0.5rem 0.75rem', color: colors.textMuted, wordBreak: 'break-all' as const }}>
                  {v}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const renderDnsPause = () => {
    if (!phase1Result) return null
    return (
      <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '1.25rem' }}>
        <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem' }}>DNS Setup Required</h2>

        <div
          style={{
            padding: '1rem',
            background: '#eff6ff',
            border: `1px solid #bfdbfe`,
            borderRadius: '6px',
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '0.5rem',
          }}
        >
          <div style={{ fontWeight: 600, color: '#1d4ed8', fontSize: '0.9375rem' }}>
            Infrastructure provisioned successfully
          </div>
          <div style={{ fontSize: '0.875rem', color: '#1e40af' }}>
            The VM is running and has a static IP. Before continuing, create a DNS A record so TLS certificates can be
            issued.
          </div>
        </div>

        <div
          style={{
            padding: '1rem',
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
          }}
        >
          <div style={{ fontSize: '0.8125rem', color: colors.textMuted, marginBottom: '0.5rem' }}>
            Create this DNS A record:
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>
                  Record
                </th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>
                  Type
                </th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>
                  Value
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '0.5rem 0.75rem' }}>
                  <code style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    {phase1Result.domain ?? '(no domain)'}
                  </code>
                </td>
                <td style={{ padding: '0.5rem 0.75rem' }}>A</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>
                  <code
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '0.9375rem',
                      fontWeight: 600,
                      color: '#1d4ed8',
                      background: '#eff6ff',
                      padding: '0.125rem 0.5rem',
                      borderRadius: '4px',
                    }}
                  >
                    {phase1Result.publicIp}
                  </code>
                </td>
              </tr>
            </tbody>
          </table>
          <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.5rem' }}>
            DNS is managed in GoDaddy for medflowllc.com. Allow 1-5 minutes for propagation.
          </div>
        </div>

        <div
          style={{
            padding: '0.75rem',
            background: '#f8fafc',
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            fontSize: '0.8125rem',
            color: '#374151',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '0.375rem' }}>Verify DNS propagation before continuing:</div>
          <code
            style={{
              display: 'block',
              background: '#1e293b',
              color: '#e2e8f0',
              padding: '0.5rem 0.75rem',
              borderRadius: '4px',
              fontSize: '0.8125rem',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap' as const,
              marginBottom: '0.375rem',
            }}
          >
            {`dig +short ${phase1Result.domain ?? '<domain>'} A`}
          </code>
          <div style={{ color: colors.textMuted }}>
            The output should show <strong>{phase1Result.publicIp}</strong>. If it returns nothing or a different IP,
            wait a few minutes and try again. You can also use{' '}
            <code style={{ fontSize: '0.8125rem' }}>nslookup {phase1Result.domain ?? '<domain>'}</code> on Windows.
          </div>
        </div>

        <div
          style={{
            padding: '0.75rem',
            background: '#f8fafc',
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            fontSize: '0.8125rem',
            color: colors.textMuted,
          }}
        >
          You can close this page and resume later from the Fleet page. The environment will appear with an
          &quot;Awaiting DNS&quot; badge.
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <button
            onClick={handleResume}
            disabled={resuming}
            style={{ ...btnStyle, background: colors.primary, color: '#fff', opacity: resuming ? 0.6 : 1 }}
            data-testid="resume-provision-btn"
          >
            {resuming ? 'Starting...' : 'DNS is ready — continue installation'}
          </button>
          <button onClick={() => navigate('/fleet')} style={{ ...btnStyle, background: '#f1f5f9', color: '#374151' }}>
            Resume later
          </button>
        </div>
      </div>
    )
  }

  const renderStepProgress = () => {
    const isDone = jobStatus?.status === 'completed'
    const isFailed = jobStatus?.status === 'failed'

    return (
      <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '1.25rem' }}>
        <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem' }}>
          {step === 4 ? 'Infrastructure Provisioning' : 'Software Installation'}
        </h2>

        {provisionSteps.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '0.5rem' }}>
            {provisionSteps.map((s, i) => {
              const clickable = s.status === 'done' || s.status === 'failed'
              const isExpanded = expandedStep === i
              const eventDetail = stepEvents[s.step]
              return (
                <div key={i}>
                  <div
                    onClick={clickable ? () => handleStepClick(i, s.step) : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.5rem 0.75rem',
                      background: colors.bgMuted,
                      borderRadius: isExpanded ? '6px 6px 0 0' : '6px',
                      fontSize: '0.875rem',
                      cursor: clickable ? 'pointer' : 'default',
                    }}
                  >
                    <StepStatusIcon status={s.status} />
                    <span
                      style={{
                        flex: 1,
                        color: s.status === 'done' ? '#16a34a' : s.status === 'failed' ? '#dc2626' : '#374151',
                        fontWeight: s.status === 'running' ? 600 : 400,
                      }}
                    >
                      {stepLabel(s.step)}
                    </span>
                    {s.elapsedMs !== undefined && (
                      <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>
                        {(s.elapsedMs / 1000).toFixed(1)}s
                      </span>
                    )}
                    {s.message && (
                      <span
                        style={{
                          color: colors.textMuted,
                          fontSize: '0.75rem',
                          maxWidth: '240px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap' as const,
                        }}
                      >
                        {s.message}
                      </span>
                    )}
                    {clickable && (
                      <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>
                        {isExpanded ? 'Hide' : 'Output'}
                      </span>
                    )}
                  </div>
                  {isExpanded && (
                    <div
                      style={{
                        background: '#1e293b',
                        borderRadius: '0 0 6px 6px',
                        padding: '0.75rem',
                        maxHeight: '300px',
                        overflow: 'auto',
                      }}
                    >
                      {eventDetail === undefined && (
                        <div style={{ color: '#94a3b8', fontSize: '0.8125rem' }}>Loading...</div>
                      )}
                      {eventDetail === null && (
                        <div style={{ color: '#94a3b8', fontSize: '0.8125rem' }}>No output recorded for this step.</div>
                      )}
                      {eventDetail && (
                        <>
                          {eventDetail.command && (
                            <div style={{ color: '#64748b', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                              $ {eventDetail.command}
                            </div>
                          )}
                          {eventDetail.stdout && (
                            <pre
                              style={{
                                margin: 0,
                                color: '#e2e8f0',
                                fontSize: '0.75rem',
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                              }}
                            >
                              {eventDetail.stdout}
                            </pre>
                          )}
                          {eventDetail.stderr && (
                            <pre
                              style={{
                                margin: eventDetail.stdout ? '0.5rem 0 0' : 0,
                                color: '#fca5a5',
                                fontSize: '0.75rem',
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                              }}
                            >
                              {eventDetail.stderr}
                            </pre>
                          )}
                          {!eventDetail.stdout && !eventDetail.stderr && (
                            <div style={{ color: '#94a3b8', fontSize: '0.8125rem' }}>No output captured.</div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {!jobId && !jobStatus && (
          <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Starting provisioning job...</div>
        )}

        {jobId && !jobStatus && (
          <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Waiting for job status...</div>
        )}

        {isDone && jobStatus?.result && (
          <div
            style={{
              padding: '1rem',
              background: colors.bgSuccess,
              border: `1px solid ${colors.borderSuccess}`,
              borderRadius: '6px',
              display: 'flex',
              flexDirection: 'column' as const,
              gap: '0.75rem',
            }}
          >
            <div style={{ fontWeight: 600, color: '#16a34a', fontSize: '0.9375rem' }}>Provisioning complete</div>
            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '0.375rem', fontSize: '0.875rem' }}>
              <div>
                <span style={{ color: colors.textMuted, fontWeight: 500 }}>Public IP: </span>
                <code>{jobStatus.result.publicIp}</code>
              </div>
              {jobStatus.result.domain && (
                <div>
                  <span style={{ color: colors.textMuted, fontWeight: 500 }}>Domain: </span>
                  <code>{jobStatus.result.domain}</code>
                </div>
              )}
              <div>
                <span style={{ color: colors.textMuted, fontWeight: 500 }}>License Key: </span>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginTop: '0.25rem',
                    background: '#fff',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '4px',
                    border: `1px solid ${colors.borderSuccess}`,
                  }}
                >
                  <code
                    style={{ flex: 1, fontSize: '0.8125rem', wordBreak: 'break-all' as const, fontFamily: 'monospace' }}
                  >
                    {jobStatus.result.plaintextKey}
                  </code>
                  <button
                    onClick={handleCopyKey}
                    style={{
                      ...btnStyle,
                      background: colors.primary,
                      color: '#fff',
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.75rem',
                    }}
                    data-testid="copy-license-key-btn"
                  >
                    {copiedKey ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
              <div>
                <span style={{ color: colors.textMuted, fontWeight: 500 }}>Environment: </span>
                <button
                  onClick={() => navigate(`/fleet/${jobStatus.result!.environmentId}`)}
                  style={{
                    ...btnStyle,
                    background: 'none',
                    color: colors.primary,
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  View in Fleet
                </button>
              </div>
            </div>
          </div>
        )}

        {isFailed && jobStatus?.error && (
          <div
            style={{
              padding: '1rem',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              display: 'flex',
              flexDirection: 'column' as const,
              gap: '0.5rem',
            }}
          >
            <div style={{ fontWeight: 600, color: '#dc2626', fontSize: '0.9375rem' }}>Provisioning failed</div>
            <div style={{ fontSize: '0.875rem', color: '#dc2626' }}>
              {(() => {
                const failed = jobStatus.steps.find((s) => s.status === 'failed')
                return failed ? `Step "${stepLabel(failed.step)}" failed: ${jobStatus.error}` : jobStatus.error
              })()}
            </div>
            <div style={{ fontSize: '0.8125rem', color: colors.textMuted, marginTop: '0.25rem' }}>
              {jobStatus.steps.some((s) => s.step === 'create-vm' && s.status !== 'pending')
                ? 'The VM may still be running. Use Fleet > Environments to manage it, or SSH in using the stored deploy key.'
                : 'No VM was created (the failure happened before the create-VM step), so there is nothing to clean up at the cloud provider.'}
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderCurrentStep = () => {
    if (step === 1) return renderStep1()
    if (step === 2) return renderStep2()
    if (step === 3) return renderStep3()
    if (step === 4 && awaitingDns) return renderDnsPause()
    return renderStepProgress()
  }

  const isProvisioning = step >= 4
  const currentStepLabel = step === 4 && awaitingDns ? 'DNS Setup' : STEP_LABELS[Math.min(step, TOTAL_STEPS) - 1]

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '1.5rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
          <button
            onClick={() => navigate('/fleet')}
            style={{ ...btnStyle, background: 'transparent', color: colors.textMuted, padding: '0' }}
          >
            &larr; Fleet
          </button>
        </div>
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Provision New Node</h1>
        <div style={{ color: colors.textMuted, fontSize: '0.875rem', marginTop: '0.25rem' }}>
          {currentStepLabel} — Step {Math.min(step, TOTAL_STEPS)} of {TOTAL_STEPS}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.25rem' }}>
        {Array.from({ length: TOTAL_STEPS }, (_, i) => {
          const n = i + 1
          const done = n < step || (n === 4 && step === 5)
          const active = n === step || (n === 4 && step === 4 && awaitingDns) || (n === 5 && step === 5)
          return (
            <div key={n} style={{ flex: 1 }}>
              <div
                style={{
                  height: '4px',
                  borderRadius: '2px',
                  background: done || active ? colors.primary : colors.border,
                  opacity: done ? 0.5 : 1,
                }}
              />
              <div
                style={{
                  fontSize: '0.6875rem',
                  color: active ? colors.primary : colors.textMuted,
                  marginTop: '0.25rem',
                  textAlign: 'center' as const,
                }}
              >
                {STEP_LABELS[i]}
              </div>
            </div>
          )
        })}
      </div>

      {stepError && (
        <div
          style={{
            padding: '0.75rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#dc2626',
            fontSize: '0.875rem',
            marginBottom: '1rem',
          }}
          data-testid="step-error"
        >
          {stepError}
        </div>
      )}

      <div style={cardStyle}>{renderCurrentStep()}</div>

      {!isProvisioning && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.25rem' }}>
          <button
            onClick={handleBack}
            disabled={step === 1}
            style={{
              ...btnStyle,
              background: '#f1f5f9',
              color: step === 1 ? colors.textMuted : '#374151',
              opacity: step === 1 ? 0.5 : 1,
            }}
            data-testid="back-btn"
          >
            Back
          </button>

          {step < 3 ? (
            <button
              onClick={handleNext}
              style={{ ...btnStyle, background: colors.primary, color: '#fff' }}
              data-testid="next-btn"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleNext}
              style={{ ...btnStyle, background: colors.primary, color: '#fff' }}
              data-testid="provision-btn"
            >
              Provision
            </button>
          )}
        </div>
      )}

      {isProvisioning && !awaitingDns && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.25rem' }}>
          {jobStatus?.status === 'completed' || jobStatus?.status === 'failed' ? (
            <button onClick={() => navigate('/fleet')} style={{ ...btnStyle, background: '#f1f5f9', color: '#374151' }}>
              Back to Fleet
            </button>
          ) : (
            <span style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Provisioning in progress...</span>
          )}
        </div>
      )}
    </div>
  )
}

function StepStatusIcon({ status }: { status: ProvisionStep['status'] }): JSX.Element {
  if (status === 'pending') {
    return (
      <span
        style={{
          display: 'inline-block',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          background: '#d1d5db',
          flexShrink: 0,
        }}
      />
    )
  }
  if (status === 'running') {
    return <SpinnerIcon />
  }
  if (status === 'done') {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          background: '#16a34a',
          color: '#fff',
          fontSize: '10px',
          flexShrink: 0,
        }}
      >
        &#10003;
      </span>
    )
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        background: '#dc2626',
        color: '#fff',
        fontSize: '10px',
        flexShrink: 0,
      }}
    >
      &#10007;
    </span>
  )
}

function SpinnerIcon(): JSX.Element {
  return (
    <>
      <style>{`
        @keyframes provision-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .provision-spinner {
          animation: provision-spin 0.8s linear infinite;
        }
      `}</style>
      <span
        className="provision-spinner"
        style={{
          display: 'inline-block',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          border: `2px solid #bfdbfe`,
          borderTopColor: colors.primary,
          flexShrink: 0,
        }}
      />
    </>
  )
}
